import React, { useRef, useState } from 'react';
import AvatarDisplay, { AvatarSize } from './AvatarDisplay';
import CropModal from './CropModal';
import { validateImageFile, readFileAsDataURL } from './avatarUtils';

export interface AvatarEditorProps {
  /** Current display name (for initials fallback) */
  name?: string | null;
  /** Current avatar URL */
  avatarUrl?: string | null;
  /**
   * Called after the user confirms the crop.
   * Consumer is responsible for uploading and persisting the blob.
   * Should resolve to the final public URL to update the preview.
   */
  onFileReady: (blob: Blob) => Promise<string>;
  size?: AvatarSize;
  /** Extra class for the outer wrapper */
  className?: string;
  disabled?: boolean;
  /** Label below the avatar. Defaults to "点击更换头像" */
  label?: string;
}

/**
 * Project‑level reusable avatar editor.
 *
 * Flow: click → validate → open crop modal → compress → onFileReady(blob) → preview
 */
export default function AvatarEditor({
  name,
  avatarUrl,
  onFileReady,
  size = 'xl',
  className = '',
  disabled = false,
  label = '点击更换头像',
}: AvatarEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(avatarUrl ?? null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // ── File picked ────────────────────────────────────────────────────────────
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError(null);
    setSuccess(false);

    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const dataUrl = await readFileAsDataURL(file);
      setCropSrc(dataUrl);
    } catch {
      setError('图片读取失败，请重试');
    }
  };

  // ── Crop confirmed ─────────────────────────────────────────────────────────
  const handleCropConfirm = async (blob: Blob) => {
    setCropSrc(null);
    setUploading(true);
    setError(null);

    // Immediate local preview
    const localUrl = URL.createObjectURL(blob);
    setPreview(localUrl);

    try {
      const finalUrl = await onFileReady(blob);
      setPreview(finalUrl);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败，请重试');
      setPreview(avatarUrl ?? null); // revert
    } finally {
      setUploading(false);
      URL.revokeObjectURL(localUrl);
    }
  };

  const handleCropCancel = () => setCropSrc(null);

  const trigger = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  return (
    <>
      <div className={`flex flex-col items-center gap-2 ${className}`}>
        {/* Avatar with hover overlay */}
        <div
          className="relative group cursor-pointer"
          onClick={trigger}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && trigger()}
          aria-label="更换头像"
        >
          <AvatarDisplay
            name={name}
            avatarUrl={preview}
            size={size}
            ring="ring-2 ring-amber-400 shadow-sm"
          />

          {/* Hover / loading overlay */}
          <div className="absolute inset-0 rounded-full bg-black/45 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            {uploading ? (
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-loader-4-line text-white text-xl animate-spin" />
              </div>
            ) : (
              <>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-camera-line text-white text-base" />
                </div>
                <span className="text-white text-[9px] mt-0.5 font-medium">更换</span>
              </>
            )}
          </div>

          {/* Success tick badge */}
          {success && (
            <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center shadow-sm">
              <i className="ri-check-line text-white text-xs" />
            </div>
          )}
        </div>

        {/* Label */}
        {label && (
          <p className="text-xs text-gray-400 select-none">
            {uploading ? '上传中…' : label}
          </p>
        )}

        {/* Error message */}
        {error && (
          <p className="text-xs text-red-500 text-center max-w-[160px]">{error}</p>
        )}

        {/* Size hint */}
        <p className="text-[10px] text-gray-300">
          支持 JPG / PNG / WebP · 自动压缩至 1MB 内
        </p>

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/bmp"
          className="hidden"
          onChange={handleFileChange}
          disabled={disabled || uploading}
        />
      </div>

      {/* Crop modal (portal‑like, rendered at end of component tree) */}
      {cropSrc && (
        <CropModal
          imageSrc={cropSrc}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </>
  );
}
